import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { EASE_EXPO, EVIL_RED, MUTED_GRAY, NOPING_GREEN } from '../constants';

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

interface LiveValues {
  value: string;
  bar: number;
}

/**
 * How many jitter ticks run before the panel lands on `final` (SCRUM-1956).
 *
 * The interval used to run for as long as the tab stayed open. Measured on the live /trial page,
 * that was 1.309 DOM mutations in a 10-second window, 1.257 of them style writes on these bars —
 * framer-motion re-animating `width` on every tick, at frame rate, forever. The SEO audit reads that
 * as main content that never reaches a final state.
 *
 * The arrival effect is what sells the comparison, so it stays: the numbers still flail on the way
 * in. They just stop somewhere, and on the same frame for every visitor.
 */
const SETTLE_AFTER_TICKS = 6;

interface FluctuatingMetric {
  metric: string;
  getValue: () => string;
  getBar: () => number;
  /** The deterministic frame the metric settles on once the jitter is done. */
  final: LiveValues;
  detail: string;
}

const WITHOUT_METRICS: FluctuatingMetric[] = [
  { metric: 'PING', getValue: () => `${randInt(120, 280)}ms`, getBar: () => randInt(60, 95), final: { value: '210ms', bar: 82 }, detail: 'Rubber-banding every fight' },
  { metric: 'PACKET LOSS', getValue: () => `${randInt(2, 30)}%`, getBar: () => randInt(40, 85), final: { value: '18%', bar: 67 }, detail: 'Constant micro-stutters' },
  { metric: 'FPS DROPS', getValue: () => `${randInt(20, 60)}%`, getBar: () => randInt(35, 75), final: { value: '44%', bar: 58 }, detail: 'Freezes at worst moments' },
  { metric: 'ROUTE', getValue: () => `${randInt(10, 18)} HOPS`, getBar: () => randInt(55, 90), final: { value: '14 HOPS', bar: 74 }, detail: 'Bouncing through 3 continents' },
];

const WITH_METRICS: FluctuatingMetric[] = [
  { metric: 'PING', getValue: () => `${randInt(1, 5)}ms`, getBar: () => randInt(3, 10), final: { value: '3ms', bar: 6 }, detail: 'Instant hit registration' },
  { metric: 'PACKET LOSS', getValue: () => '0%', getBar: () => 2, final: { value: '0%', bar: 2 }, detail: 'Zero. None. Gone.' },
  { metric: 'FPS DROPS', getValue: () => '0%', getBar: () => 2, final: { value: '0%', bar: 2 }, detail: 'Locked and smooth' },
  { metric: 'ROUTE', getValue: () => '3 HOPS', getBar: () => 15, final: { value: '3 HOPS', bar: 15 }, detail: 'Direct optimized path' },
];

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Panel({ side, baseDelay }: { side: 'before' | 'after'; baseDelay: number }) {
  const { t } = useTranslation(['components/trial/lp/v2a']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const color = side === 'before' ? EVIL_RED : NOPING_GREEN;
  const label = side === 'before' ? t('before_after.without') : t('before_after.with');
  const decorChar = side === 'before' ? 'X' : '\u2713';

  const fluctuatingDefs = side === 'before' ? WITHOUT_METRICS : WITH_METRICS;
  const interval = side === 'before' ? 800 : 1200;

  const [liveValues, setLiveValues] = useState<LiveValues[]>(fluctuatingDefs.map(() => ({ value: '---', bar: 0 })));

  useEffect(() => {
    const settle = () => setLiveValues(fluctuatingDefs.map((definition) => definition.final));
    const jitter = () => setLiveValues(fluctuatingDefs.map((definition) => ({ value: definition.getValue(), bar: definition.getBar() })));

    // Off screen, or for a reader who asked for less motion, go straight to the resting frame —
    // never to a random one, which is what the old unconditional call above produced.
    if (!inView || prefersReducedMotion()) {
      settle();
      return undefined;
    }

    jitter();

    let ticksLeft = SETTLE_AFTER_TICKS;
    const id = setInterval(() => {
      ticksLeft -= 1;
      if (ticksLeft <= 0) {
        clearInterval(id);
        settle();
        return;
      }
      jitter();
    }, interval);

    return () => clearInterval(id);
  }, [inView, interval, fluctuatingDefs]);

  return (
    <motion.div
      ref={ref}
      className='relative overflow-hidden p-8 md:p-12'
      style={{
        background: `linear-gradient(135deg, ${color}14, ${color}05)`,
        borderLeft: side === 'before' ? `3px solid ${color}` : 'none',
        borderRight: side === 'after' ? `3px solid ${color}` : 'none',
        borderTop: `1px solid ${color}26`,
        borderBottom: `1px solid ${color}26`,
      }}
      initial={{ x: side === 'before' ? -120 : 120, opacity: 0, skewX: side === 'before' ? 6 : -6 }}
      animate={inView ? { x: 0, opacity: 1, skewX: 0 } : undefined}
      transition={{ duration: 0.7, ease: EASE_EXPO }}
    >
      <div className='mb-8 flex items-center gap-3'>
        <div className='size-3 animate-pulse rounded-full' style={{ background: color }} />
        <h3 className='text-2xl font-bold tracking-[0.15em] md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color }}>
          {label}
        </h3>
      </div>

      <div className='space-y-5'>
        {fluctuatingDefs.map((item, i) => (
          <motion.div key={item.metric} initial={{ y: 60, opacity: 0 }} animate={inView ? { y: 0, opacity: 1 } : undefined} transition={{ duration: 0.7, delay: baseDelay + i * 0.08, ease: EASE_EXPO }}>
            <div className='mb-1.5 flex items-baseline justify-between'>
              <span className='text-[10px] font-semibold tracking-[0.2em]' style={{ fontFamily: 'var(--font-outfit)', color: `${color}80` }}>
                {item.metric}
              </span>
              <span className='text-2xl font-bold tabular-nums md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color }}>
                {liveValues[i]?.value}
              </span>
            </div>
            <div className='h-2 w-full overflow-hidden rounded-full bg-white/5'>
              <motion.div className='h-full rounded-full' style={{ background: `linear-gradient(90deg, ${color}, ${color}99)` }} animate={{ width: `${liveValues[i]?.bar ?? 0}%` }} transition={{ duration: 0.5, ease: 'easeOut' as const }} />
            </div>
            <p className='mt-1 text-xs' style={{ fontFamily: 'var(--font-outfit)', color: `${MUTED_GRAY}99` }}>
              {item.detail}
            </p>
          </motion.div>
        ))}
      </div>

      <div className='absolute -bottom-4 -end-2 select-none text-[120px] font-bold leading-none' style={{ fontFamily: 'var(--font-bai)', color: `${color}0A` }}>
        {decorChar}
      </div>
    </motion.div>
  );
}

export function BeforeAfterPanel() {
  return (
    <div className='mx-auto grid max-w-6xl grid-cols-1 gap-0 md:grid-cols-2'>
      <Panel side='before' baseDelay={0.1} />
      <Panel side='after' baseDelay={0.25} />
    </div>
  );
}
